import React from "react";
import { Container, Row, Col } from "react-bootstrap";
import Breadcrumbs from "../../../Common/Breadcrumbs";
import EnrolmentsFilters from "../../../components/academyEnrolments/molecules/EnrolmentsFilters";
import EnrolmentsTable from "../../../components/academyEnrolments/organisms/EnrolmentsTable";
import BulkUploadModal from "../../../components/academyEnrolments/organisms/BulkUploadModal";
import { DeleteModal } from "../../../Common/DeleteModal";
import { useAcademyEnrolments } from "./useAcademyEnrolments";
import "../AcademyEnrolments.css";
import "./Enrolments.css";

const AcademyEnrolmentsPage: React.FC = () => {
  const { functions, values, loading, error, setters } = useAcademyEnrolments();

  return (
    <div className="page-content">
      <Container fluid>
        {/* Breadcrumbs */}
        <div className="mb-3 mt-4" style={{ padding: "0 15px" }}>
          <Breadcrumbs
            items={[
              { label: "Academy" },
              { label: "Enrolments Management", active: true },
            ]}
          />
        </div>

        {/* Header + Filters */}
        <div style={{ padding: "0 15px", marginBottom: "20px" }}>
          <Row className="align-items-center justify-content-between g-3 mb-3">
            <Col xs="12" md="auto">
              <h2 className="mb-0">Enrolments</h2>
              <p className="text-muted mb-0" style={{ fontSize: 13 }}>
                {`${values.totalCount} total enrolment${values.totalCount !== 1 ? "s" : ""}`}
              </p>
            </Col>
          </Row>

          <EnrolmentsFilters
            searchValue={values.search}
            onSearchChange={setters.setSearch}
            enrolledThroughValue={values.enrolledThroughFilter}
            onEnrolledThroughChange={setters.setEnrolledThroughFilter}
            sportValue={values.sportFilter}
            onSportChange={setters.setSportFilter}
            sportOptions={values.sportOptions}
            dateRange={values.dateRange}
            onDateRangeChange={setters.setDateRange}
            paidAt={values.paidAt}
            onPaidAtChange={setters.setPaidAt}
            onAddEnrolmentClick={functions.handleAddEnrolment}
            onBulkUploadClick={functions.handleOpenUploadModal}
            onDownloadClick={functions.handleDownload}
            selectedCount={values.selectedIds.size}
            onBulkDeleteClick={functions.handleRequestDeleteBulk}
          />
        </div>

        {/* Enrolments table */}
        <div style={{ padding: "0 15px" }}>
          <EnrolmentsTable
            enrolments={values.enrolments}
            loading={loading.isPageLoading}
            error={error}
            page={values.page}
            totalPages={values.totalPages}
            pageSize={values.pageSize}
            totalCount={values.totalCount}
            onPageChange={setters.setPage}
            onPageSizeChange={(size) => {
              setters.setPageSize(size);
              setters.setPage(1);
            }}
            onViewEnrolment={functions.handleViewEnrolment}
            onEditEnrolment={functions.handleEditEnrolment}
            onDeleteEnrolment={functions.handleRequestDeleteSingle}
            onToggleActive={functions.handleRequestToggleActive}
            selectedIds={values.selectedIds}
            onToggleSelectOne={functions.toggleSelectOne}
            onToggleSelectAllOnPage={functions.toggleSelectAllOnPage}
          />
        </div>

        {/* Bulk Upload Modal */}
        <BulkUploadModal
          show={values.uploadModalOpen}
          onHide={functions.handleCloseUploadModal}
          onSuccess={functions.refetchEnrolments}
        />

        {/* Delete Confirmation Modal */}
        <DeleteModal
          show={values.deleteTarget !== null}
          handleClose={functions.handleCancelDelete}
          deleteModalFunction={functions.handleConfirmDelete}
          title={
            (values.deleteTarget?.ids.length ?? 0) > 1
              ? `Delete ${values.deleteTarget?.ids.length} enrolments?`
              : "Delete this enrolment?"
          }
          message={
            (values.deleteTarget?.ids.length ?? 0) > 1
              ? `Are you sure you want to delete ${values.deleteTarget?.ids.length} selected enrolment(s)? This action cannot be undone.`
              : "Are you sure you want to delete this enrolment? This action cannot be undone."
          }
          confirmLabel="Delete"
          isLoading={values.isDeleting}
          error={values.deleteError}
        />

        {/* Toggle Active/Inactive Confirmation Modal */}
        <DeleteModal
          show={values.toggleTarget !== null}
          handleClose={functions.handleCancelToggle}
          deleteModalFunction={functions.handleConfirmToggle}
          showIcon={false}
          title={
            values.toggleTarget?.isActive
              ? "Mark enrolment inactive?"
              : "Mark enrolment active?"
          }
          message={
            values.toggleTarget?.isActive
              ? "This enrolment will be marked inactive."
              : "This enrolment will be marked active."
          }
          confirmLabel="Confirm"
          isLoading={values.isToggling}
          error={values.toggleError}
        />
      </Container>
    </div>
  );
};

export default AcademyEnrolmentsPage;
